/**
 * Phase 1: Smart Pricing Calculator - Amazon Revenue Calculator Inspired
 * Two-column layout with comprehensive cost breakdown and real-time calculations
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Calculator, DollarSign, TrendingUp, TrendingDown, AlertTriangle, 
  Download, Save, RotateCcw, Eye, EyeOff, ChevronDown, ChevronUp,
  PieChart, BarChart3, FileSpreadsheet, Package, Truck, Building2,
  ShoppingCart, Megaphone, Settings, Info
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

// Backend API configuration
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1`;

const SmartPricingCalculator = () => {
  // ============ STATE MANAGEMENT ============
  
  // Core calculation inputs
  const [sku, setSku] = useState('');
  const [sellingPrice, setSellingPrice] = useState('');
  const [costBreakdown, setCostBreakdown] = useState({
    manufacturing_cost: { name: 'Manufacturing Cost', value: 0, basis_type: 'manual', is_missing: false },
    international_freight: { name: 'International Freight', value: 0, basis_type: 'manual', is_missing: true },
    import_duties: { name: 'Import Duties & Tariffs', value: 0, basis_type: 'manual', is_missing: true },
    inbound_handling: { name: 'Inbound Handling', value: 0, basis_type: 'manual', is_missing: false },
    domestic_freight: { name: 'Domestic Freight', value: 0, basis_type: 'manual', is_missing: false },
    warehousing_cost: { name: 'Warehousing & Storage', value: 0, basis_type: 'manual', is_missing: true },
    outbound_freight: { name: 'Outbound Shipping', value: 0, basis_type: 'manual', is_missing: false },
    fulfillment_fees: { name: 'Fulfillment Fees (FBA/3PL)', value: 0, basis_type: 'manual', is_missing: true },
    platform_fees: { name: 'Platform Fees', value: 0, basis_type: 'manual', is_missing: false },
    advertising_expense: { name: 'PPC/Advertising', value: 0, basis_type: 'manual', is_missing: false },
    optional_addons: { name: 'Optional Add-ons', value: 0, basis_type: 'manual', is_missing: false },
    overhead_allocation: { name: 'Overhead Allocation', value: 0, basis_type: 'system_driven', description: 'Auto-calculated in Phase 2' }
  });
  
  // Calculation results
  const [calculation, setCalculation] = useState(null);
  const [priceSimulations, setPriceSimulations] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState('waterfall'); // waterfall, pie
  const [expandedSections, setExpandedSections] = useState(['manufacturing', 'fulfillment']);
  const [simulationMode, setSimulationMode] = useState(false);
  const [priceRange, setPriceRange] = useState([10, 100]);
  
  // Template state
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  
  // ============ COST CATEGORY GROUPS ============
  
  const costCategories = {
    manufacturing: {
      title: 'Manufacturing & Sourcing',
      icon: Package,
      color: 'blue',
      fields: ['manufacturing_cost', 'international_freight', 'import_duties']
    },
    logistics: {
      title: 'Handling & Logistics',
      icon: Truck,
      color: 'green',
      fields: ['inbound_handling', 'domestic_freight', 'warehousing_cost', 'outbound_freight']
    },
    fulfillment: {
      title: 'Fulfillment & Marketplace',
      icon: Building2,
      color: 'purple',
      fields: ['fulfillment_fees', 'platform_fees']
    },
    marketing: {
      title: 'Marketing & Operations',
      icon: Megaphone,
      color: 'orange',
      fields: ['advertising_expense', 'optional_addons']
    },
    overhead: {
      title: 'Overhead & System',
      icon: Settings,
      color: 'gray',
      fields: ['overhead_allocation']
    }
  };
  
  // ============ PHASE 2: FETCH ALLOCATED OVERHEAD ============
  
  const fetchAllocatedOverhead = useCallback(async (skuId) => {
    if (!skuId) return 0;
    
    try {
      // Get current period (YYYY-MM format)
      const currentPeriod = new Date().toISOString().slice(0, 7);
      
      // Fetch allocated overhead for this SKU
      const response = await axios.get(`${BACKEND_URL}/api/v1/finance/allocation-results/${currentPeriod}?sku=${skuId}`);
      
      if (response.data && response.data.length > 0) {
        const allocation = response.data[0];
        return parseFloat(allocation.total_overhead_cost) || 0;
      }
      
      return 0;
    } catch (error) {
      console.warn('Could not fetch allocated overhead for SKU:', skuId, error.message);
      return 0;
    }
  }, []);

  // ============ REAL-TIME CALCULATION WITH OVERHEAD INTEGRATION ============
  
  const debouncedCalculate = useCallback(
    debounce(async (currentSku, currentPrice, currentCosts) => {
      // Enhanced validation before calculation
      if (!currentSku || !currentSku.trim()) {
        setCalculation(null);
        return;
      }
      
      const price = parseFloat(currentPrice);
      if (!currentPrice || price <= 0 || isNaN(price)) {
        setCalculation(null);
        if (currentPrice && price <= 0) {
          toast.error('Selling price must be greater than zero');
        }
        return;
      }
      
      try {
        setLoading(true);
        
        // Phase 2: Fetch allocated overhead automatically
        const allocatedOverhead = await fetchAllocatedOverhead(currentSku);
        
        // Update overhead allocation in cost breakdown
        const updatedCosts = {
          ...currentCosts,
          overhead_allocation: {
            ...currentCosts.overhead_allocation,
            value: allocatedOverhead,
            is_missing: allocatedOverhead === 0,
            description: allocatedOverhead > 0 
              ? `Auto-calculated from Finance allocation (${new Date().toISOString().slice(0, 7)})`
              : 'No overhead allocated for current period'
          }
        };
        
        // Update state with new overhead allocation
        setCostBreakdown(updatedCosts);
        
        // Validate cost breakdown structure before sending
        const validatedCosts = {};
        Object.keys(updatedCosts).forEach(key => {
          const component = updatedCosts[key];
          validatedCosts[key] = {
            name: component.name || key.replace(/_/g, ' '),
            value: parseFloat(component.value) || 0,
            basis_type: component.basis_type || 'manual',
            description: component.description || '',
            is_missing: component.is_missing !== undefined ? component.is_missing : (parseFloat(component.value) === 0)
          };
        });
        
        const response = await axios.post(`${API}/calc/pricing/smart-calculate`, {
          sku: currentSku.trim(),
          selling_price: parseFloat(currentPrice),
          cost_breakdown: validatedCosts,
          save_as_template: saveAsTemplate,
          template_name: templateName || `${currentSku}_auto`
        });
        
        setCalculation(response.data);
        
        // Show notification if overhead was auto-populated
        if (allocatedOverhead > 0) {
          toast.success(`Overhead allocation updated: $${allocatedOverhead.toFixed(2)} from Finance system`);
        }
        
        // Update price range for simulation based on current price
        const price = parseFloat(currentPrice);
        setPriceRange([Math.max(1, price * 0.5), price * 1.5]);
        
      } catch (error) {
        console.error('Calculation error:', error);
        
        // Enhanced error handling with specific messages
        let errorMessage = 'Calculation failed';
        
        if (error.response) {
          const status = error.response.status;
          const detail = error.response.data?.detail || 'Unknown server error';
          
          switch (status) {
            case 400:
              errorMessage = `Input Error: ${detail}`;
              break;
            case 401:
              errorMessage = 'Authentication required. Please log in.';
              break;
            case 402:
              // Don't show subscription errors as toast - handled by subscription modal
              return;
            case 403:
              errorMessage = 'Access denied. Please check your permissions.';
              break;
            case 404:
              errorMessage = `SKU "${currentSku}" not found in the system.`;
              break;
            case 500:
              errorMessage = `Server Error: ${detail}`;
              break;
            default:
              errorMessage = `Error ${status}: ${detail}`;
          }
        } else if (error.request) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else {
          errorMessage = `Client Error: ${error.message}`;
        }
        
        toast.error(errorMessage);
        
        // Clear calculation on error to prevent stale data
        setCalculation(null);
      } finally {
        setLoading(false);
      }
    }, 300), // 300ms debounce as specified
    [saveAsTemplate, templateName, fetchAllocatedOverhead]
  );
  
  // Trigger calculation when inputs change
  useEffect(() => {
    debouncedCalculate(sku, sellingPrice, costBreakdown);
  }, [sku, sellingPrice, costBreakdown, debouncedCalculate]);
  
  // ============ HELPER FUNCTIONS ============
  
  const updateCostComponent = (componentKey, value) => {
    setCostBreakdown(prev => ({
      ...prev,
      [componentKey]: {
        ...prev[componentKey],
        value: parseFloat(value) || 0,
        is_missing: parseFloat(value) === 0
      }
    }));
  };
  
  const getTotalCost = () => {
    return Object.values(costBreakdown || {}).reduce((sum, component) => sum + (component?.value || 0), 0);
  };
  
  const getMarginColor = (marginPct) => {
    if (marginPct >= 40) return 'text-green-600 bg-green-50';
    if (marginPct >= 20) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };
  
  const getMissingComponents = () => {
    return Object.entries(costBreakdown)
      .filter(([key, component]) => component.is_missing)
      .map(([key, component]) => component.name);
  };
  
  // ============ PRICE SIMULATION ============
  
  const runPriceSimulation = async () => {
    if (!sku || priceRange[0] >= priceRange[1]) return;
    
    try {
      setLoading(true);
      
      const response = await axios.post(`${API}/calc/pricing/simulate-prices`, {
        sku: sku,
        cost_breakdown: costBreakdown,
        min_price: priceRange[0],
        max_price: priceRange[1],
        step_size: (priceRange[1] - priceRange[0]) / 20 // 20 data points
      });
      
      setPriceSimulations(response.data);
      setSimulationMode(true);
      
    } catch (error) {
      console.error('Simulation error:', error);
      toast.error('Price simulation failed');
    } finally {
      setLoading(false);
    }
  };
  
  // ============ EXPORT FUNCTIONS ============
  
  const exportToCSV = () => {
    if (!calculation) return;
    
    const csvData = [
      ['Field', 'Value'],
      ['SKU', calculation.sku],
      ['Selling Price', calculation.selling_price],
      ...Object.entries(calculation.cost_breakdown).map(([key, component]) => [
        component.name, component.value
      ]),
      ['Total Landed Cost', calculation.total_landed_cost],
      ['COGS', calculation.cogs],
      ['Gross Profit', calculation.gross_profit],
      ['Net Profit', calculation.net_profit],
      ['Gross Margin %', calculation.gross_margin_pct],
      ['Net Margin %', calculation.net_margin_pct],
      ['Margin Category', calculation.margin_category],
      ['Calculated At', calculation.calculated_at]
    ];
    
    const csv = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pricing_calculation_${calculation.sku}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };
  
  const resetToDefaults = () => {
    setSku('');
    setSellingPrice('');
    setCostBreakdown(prev => {
      const reset = {};
      Object.keys(prev).forEach(key => {
        reset[key] = {
          ...prev[key],
          value: 0,
          is_missing: key !== 'manufacturing_cost' // Only manufacturing cost is not missing by default
        };
      });
      return reset;
    });
    setCalculation(null);
    setPriceSimulations([]);
    setSimulationMode(false);
  };
  
  // ============ RENDER COMPONENT ============
  
  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Calculator className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold">Smart Pricing Calculator</h1>
            <p className="text-gray-600">Comprehensive cost breakdown and profitability analysis</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={exportToCSV}
            disabled={!calculation}
            className="gap-2"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={resetToDefaults}
            className="gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </Button>
        </div>
      </div>
      
      {/* Main Layout: Two-Column (Amazon-inspired) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* LEFT COLUMN: Cost Inputs */}
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Cost Breakdown
            </CardTitle>
            <CardDescription>
              Enter all cost components for accurate profitability analysis
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="sku">Product SKU</Label>
                <Input
                  id="sku"
                  value={sku}
                  onChange={(e) => setSku(e.target.value)}
                  placeholder="Enter SKU"
                />
              </div>
              <div>
                <Label htmlFor="price">Selling Price ($)</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={sellingPrice}
                  onChange={(e) => setSellingPrice(e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>
            
            {/* Cost Categories - Accordion Layout */}
            <Accordion type="multiple" value={expandedSections} onValueChange={setExpandedSections}>
              {Object.entries(costCategories).map(([categoryKey, category]) => {
                const IconComponent = category.icon;
                const missingCount = category.fields.filter(field => 
                  costBreakdown[field]?.is_missing
                ).length;
                
                return (
                  <AccordionItem key={categoryKey} value={categoryKey}>
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-3">
                          <IconComponent className={`w-5 h-5 text-${category.color}-600`} />
                          <span className="font-medium">{category.title}</span>
                          {missingCount > 0 && (
                            <Badge variant="secondary" className="bg-yellow-100 text-yellow-700">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              {missingCount} missing
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          ${category.fields.reduce((sum, field) => sum + (costBreakdown[field]?.value || 0), 0).toFixed(2)}
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pl-8">
                        {category.fields.map(fieldKey => {
                          const component = costBreakdown[fieldKey];
                          return (
                            <div key={fieldKey} className="grid grid-cols-3 gap-3 items-center">
                              <div className="flex items-center gap-2">
                                <Label className="text-sm">{component.name}</Label>
                                {component.is_missing && (
                                  <AlertTriangle 
                                    className="w-4 h-4 text-yellow-500" 
                                    title={`Add ${component.name} to complete cost analysis`}
                                  />
                                )}
                              </div>
                              <Input
                                type="number"
                                step="0.01"
                                value={component.value}
                                onChange={(e) => updateCostComponent(fieldKey, e.target.value)}
                                placeholder="0.00"
                                className="text-right"
                              />
                              <div className="text-xs text-gray-500">
                                {component.description && (
                                  <span title={component.description}>
                                    <Info className="w-3 h-3 inline mr-1" />
                                    {component.basis_type}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
            
            {/* Price Simulation Controls */}
            {!simulationMode && sku && sellingPrice && (
              <div className="border-t pt-4">
                <div className="space-y-3">
                  <Label>Price Simulation Range ($)</Label>
                  <Slider
                    value={priceRange}
                    onValueChange={setPriceRange}
                    min={1}
                    max={200}
                    step={1}
                    className="w-full"
                  />
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>${priceRange[0]}</span>
                    <span>${priceRange[1]}</span>
                  </div>
                  <Button 
                    onClick={runPriceSimulation}
                    disabled={loading}
                    className="w-full gap-2"
                  >
                    <BarChart3 className="w-4 h-4" />
                    Simulate Price Impact
                  </Button>
                </div>
              </div>
            )}
            
          </CardContent>
        </Card>
        
        {/* RIGHT COLUMN: Results & Visualization */}
        <Card className="h-fit">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Profitability Analysis
                </CardTitle>
                <CardDescription>
                  Real-time calculation results and margin analysis
                </CardDescription>
              </div>
              
              {calculation && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setActiveView(activeView === 'waterfall' ? 'pie' : 'waterfall')}
                  >
                    {activeView === 'waterfall' ? <PieChart className="w-4 h-4" /> : <BarChart3 className="w-4 h-4" />}
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            
            {loading && (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-500 mt-2">Calculating...</p>
              </div>
            )}
            
            {!loading && !calculation && (
              <div className="text-center py-8">
                <Calculator className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Enter SKU and selling price to see analysis</p>
              </div>
            )}
            
            {!loading && calculation && (
              <div className="space-y-6">
                
                {/* KPI Summary */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      ${parseFloat(calculation.net_profit || 0).toFixed(2)}
                    </div>
                    <div className="text-sm text-blue-700">Net Profit</div>
                  </div>
                  <div className={`text-center p-4 rounded-lg ${getMarginColor(parseFloat(calculation.net_margin_pct || 0))}`}>
                    <div className="text-2xl font-bold">
                      {parseFloat(calculation.net_margin_pct || 0).toFixed(1)}%
                    </div>
                    <div className="text-sm">Net Margin</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-xl font-semibold text-gray-700">
                      ${parseFloat(calculation.total_landed_cost || 0).toFixed(2)}
                    </div>
                    <div className="text-sm text-gray-600">Total Cost</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-xl font-semibold text-gray-700">
                      ${parseFloat(calculation.cogs || 0).toFixed(2)}
                    </div>
                    <div className="text-sm text-gray-600">COGS</div>
                  </div>
                </div>
                
                {/* Missing Data Warnings */}
                {calculation.missing_data_warnings?.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-600" />
                      <span className="font-medium text-yellow-800">Missing Cost Data</span>
                    </div>
                    <ul className="text-sm text-yellow-700 space-y-1">
                      {calculation.missing_data_warnings.map((warning, idx) => (
                        <li key={idx}>• {warning}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Visualization Placeholder */}
                <div className="bg-gray-50 rounded-lg p-6 text-center">
                  <div className="mb-4">
                    {activeView === 'waterfall' ? (
                      <BarChart3 className="w-12 h-12 text-gray-400 mx-auto" />
                    ) : (
                      <PieChart className="w-12 h-12 text-gray-400 mx-auto" />
                    )}
                  </div>
                  <p className="text-gray-500 font-medium">
                    {activeView === 'waterfall' ? 'Waterfall Chart' : 'Cost Distribution Pie Chart'}
                  </p>
                  <p className="text-sm text-gray-400 mt-1">
                    Visual breakdown of cost components and profit margins
                  </p>
                  <p className="text-xs text-blue-600 mt-2">
                    Chart implementation coming next...
                  </p>
                </div>
                
                {/* Margin Category Badge */}
                <div className="text-center">
                  <Badge 
                    variant={calculation.margin_category === 'healthy' ? 'default' : 
                           calculation.margin_category === 'moderate' ? 'secondary' : 'destructive'}
                    className="text-lg px-4 py-2"
                  >
                    {calculation.margin_category === 'healthy' && '🟢 Healthy Margin (>40%)'}
                    {calculation.margin_category === 'moderate' && '🟡 Moderate Margin (20-40%)'}
                    {calculation.margin_category === 'critical' && '🔴 Critical Margin (<20%)'}
                  </Badge>
                </div>
                
              </div>
            )}
          </CardContent>
        </Card>
        
      </div>
      
    </div>
  );
};

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default SmartPricingCalculator;